import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Calendar,
  BarChart3,
  Clock,
  Eye,
  TrendingUp,
  Users,
  Zap,
  Archive,
  Sparkles,
  LayoutList,
} from "lucide-react";
import { ClientLogo } from "../../components/ClientLogo";
import type { SocialCampaignReportResponse } from "../../types";
import { MetricCard } from "../../components/MetricCard";
import { DonutChart } from "../../components/DonutChart";
import { BarChart } from "../../components/BarChart";
import { SectionToggleWrapper } from "../../components/SectionToggleWrapper";
import { CARD_BG, PAGE_BG, SHADOW } from "../../config/theme";
import { formatReportDateLabel } from "../../utils/dateFormatting";
import {
  buildSocialLayoutViewModel,
  EXCLUDED_INFLUENCER_AD_NAMES,
  type SocialCampaignView,
} from "./socialLayoutViewModel";

interface SocialLayoutProps {
  data: SocialCampaignReportResponse;
  reportId?: string;
  onNewUpload?: () => void;
  clientId?: string;
}

export type EngagementRateMode = "reach" | "impressions" | "followers";

const ENGAGEMENT_RATE_OPTIONS: Array<{
  id: EngagementRateMode;
  label: string;
  helper: string;
}> = [
  {
    id: "reach",
    label: "Reach",
    helper: "Engagement divided by reached users.",
  },
  {
    id: "impressions",
    label: "Impressions",
    helper: "Engagement divided by impressions.",
  },
  {
    id: "followers",
    label: "Followers",
    helper:
      "Engagement divided by follower count when available or manually entered.",
  },
];

const parseCtrValue = (
  value: string | number | null | undefined,
): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const numeric =
    typeof value === "number" ? value : Number.parseFloat(String(value));
  if (!Number.isFinite(numeric) || numeric < 0) {
    return null;
  }
  return numeric;
};

export const selectEngagementDenominator = <
  T extends {
    total_reach?: number;
    total_impressions?: number;
    total_followers?: number | null;
  },
>(
  entity: T,
  mode: EngagementRateMode,
  manualFollowersOverride?: number | null,
): number | null => {
  switch (mode) {
    case "impressions":
      return entity.total_impressions ?? null;
    case "followers":
      if (
        manualFollowersOverride !== undefined &&
        manualFollowersOverride !== null &&
        manualFollowersOverride > 0
      ) {
        return manualFollowersOverride;
      }
      return entity.total_followers ?? null;
    case "reach":
    default:
      return entity.total_reach ?? null;
  }
};

export const calculateEngagementRatePercent = <
  T extends {
    total_engagement: number;
    total_reach?: number;
    total_impressions?: number;
    total_followers?: number | null;
  },
>(
  entity: T,
  mode: EngagementRateMode,
  manualFollowersOverride?: number | null,
): number | null => {
  const denominator = selectEngagementDenominator(
    entity,
    mode,
    manualFollowersOverride,
  );
  if (!denominator || denominator <= 0) {
    return null;
  }
  return (entity.total_engagement / denominator) * 100;
};

export const formatEngagementRatePercent = (value: number | null): string =>
  value === null ? "—" : `${value.toFixed(2)}%`;

export const engagementBadgeClasses = (value: number | null): string => {
  if (value === null) {
    return "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200";
  }
  if (value >= 3) {
    return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
  }
  if (value >= 1) {
    return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
  }
  return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
};

/**
 * Specialized layout for Meta Social Media (Facebook/Instagram) campaigns.
 *
 * Displays engagement-focused metrics:
 * - Platform comparison (Facebook vs Instagram)
 * - Engagement metrics (reactions, comments, shares)
 * - Reach and awareness metrics
 * - Ad set performance
 */
export const SocialLayout: React.FC<SocialLayoutProps> = ({
  data,
  reportId,
  clientId,
}) => {
  const resolvedClientId = clientId ?? "ajinomoto";

  // Campaign view toggle (active vs archive vs all)
  const [campaignView, setCampaignView] = useState<SocialCampaignView>("active");

  // Filter toggle for excluding influencer ads from reporting.
  const [excludeInfluencerAds, setExcludeInfluencerAds] = useState<boolean>(true);

  const [hiddenSections, setHiddenSections] = useState<Record<string, boolean>>(
    {},
  );
  const [engagementRateMode, setEngagementRateMode] =
    useState<EngagementRateMode>("reach");
  const [manualFollowerCount, setManualFollowerCount] = useState<number | null>(
    null,
  );
  const previousReportIdRef = useRef<string | undefined>(undefined);

  const hideSection = (sectionId: string) =>
    setHiddenSections((prev) => ({ ...prev, [sectionId]: true }));

  const restoreSection = (sectionId: string) =>
    setHiddenSections((prev) => ({ ...prev, [sectionId]: false }));

  const isSectionHidden = (sectionId: string) =>
    Boolean(hiddenSections[sectionId]);

  const {
    summary,
    platforms,
    campaigns,
    ad_sets,
    ad_creatives,
    audience_creatives,
    daily_metrics,
  } = useMemo(
    () =>
      buildSocialLayoutViewModel(data, {
        campaignView,
        excludeInfluencerAds,
      }),
    [campaignView, data, excludeInfluencerAds],
  );

  const audienceCreativePerformance = audience_creatives;

  useEffect(() => {
    const datasetFollowers = summary.total_followers ?? null;
    const reportChanged =
      reportId !== undefined && reportId !== previousReportIdRef.current;
    if (reportChanged) {
      setManualFollowerCount(datasetFollowers);
      previousReportIdRef.current = reportId;
      return;
    }
    if (
      manualFollowerCount === null &&
      datasetFollowers !== null &&
      datasetFollowers > 0
    ) {
      setManualFollowerCount(datasetFollowers);
    }
  }, [reportId, summary.total_followers, manualFollowerCount]);

  // Platform colors
  const getPlatformColor = (platform: string): string => {
    const lower = platform.toLowerCase();
    if (lower === "facebook") return "#1877F2";
    if (lower === "instagram") return "#FBBF24"; // Gold-yellow for Instagram
    return "#8b5cf6"; // fallback purple
  };

  // Prepare platform donut chart data
  const platformChartData = platforms.map((p) => ({
    brand_name: p.platform.charAt(0).toUpperCase() + p.platform.slice(1),
    units: p.total_impressions,
    percentage:
      summary.total_impressions > 0 ? (p.total_impressions / summary.total_impressions) * 100 : 0,
  }));
  const platformChartColors = platformChartData.map((entry) =>
    getPlatformColor(entry.brand_name),
  );

  // Prepare engagement breakdown chart
  const engagementLabels = ["Reactions", "Comments", "Shares"];
  const engagementValues = [
    summary.total_reactions,
    summary.total_comments,
    summary.total_shares,
  ];
  const engagementColors = ["#10b981", "#3b82f6", "#f59e0b"];

  // Prepare daily metrics chart
  const dailyLabels = daily_metrics.map((d) =>
    formatReportDateLabel(d.report_date),
  );
  const dailyDatasets = [
    {
      label: "Reach",
      data: daily_metrics.map((d) => d.total_reach),
      backgroundColor: "rgba(59, 130, 246, 0.8)",
    },
    {
      label: "Engagement",
      data: daily_metrics.map((d) => d.total_engagement),
      backgroundColor: "rgba(16, 185, 129, 0.8)",
    },
  ];

  const formatNumber = (value: number): string => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}k`;
    }
    return value.toLocaleString();
  };

  const formatPercent = (value: number | null): string =>
    value === null ? "—" : `${value.toFixed(2)}%`;

  const topAudienceLimit = 8;
  const hasAudienceData = Array.isArray(ad_sets) && ad_sets.length > 0;
  const topAudiencesByReach = hasAudienceData
    ? [...ad_sets]
        .sort((a, b) => b.total_reach - a.total_reach)
        .slice(0, topAudienceLimit)
    : [];
  const topAudiencesByCtr = hasAudienceData
    ? [...ad_sets]
        .filter((adSet) => parseCtrValue(adSet.avg_ctr) !== null)
        .sort((a, b) => {
          const ctrB = parseCtrValue(b.avg_ctr) ?? 0;
          const ctrA = parseCtrValue(a.avg_ctr) ?? 0;
          return ctrB - ctrA;
        })
        .slice(0, topAudienceLimit)
    : [];

  const audienceReachLabels = topAudiencesByReach.map(
    (adSet) => adSet.ad_set_name,
  );
  const audienceReachValues = topAudiencesByReach.map(
    (adSet) => adSet.total_reach,
  );

  const audienceCtrLabels = topAudiencesByCtr.map((adSet) => adSet.ad_set_name);
  const audienceCtrValues = topAudiencesByCtr.map((adSet) => {
    const ctr = parseCtrValue(adSet.avg_ctr) ?? 0;
    return Math.max(ctr * 100, 0);
  });

  const topAdCreativesByReach = [...ad_creatives]
    .sort((a, b) => b.total_reach - a.total_reach)
    .slice(0, topAudienceLimit);
  const minImpressionsForCtrChart = 100000;
  const topAdCreativesByCtr = [...ad_creatives]
    .filter(
      (creative) =>
        parseCtrValue(creative.avg_ctr) !== null &&
        creative.total_impressions >= minImpressionsForCtrChart,
    )
    .sort((a, b) => {
      const ctrB = parseCtrValue(b.avg_ctr) ?? 0;
      const ctrA = parseCtrValue(a.avg_ctr) ?? 0;
      return ctrB - ctrA;
    })
    .slice(0, topAudienceLimit);

  const hasAdCreativeData =
    topAdCreativesByReach.length > 0 || topAdCreativesByCtr.length > 0;
  const adCreativeReachLabels = topAdCreativesByReach.map(
    (creative) => creative.ad_name,
  );
  const adCreativeReachValues = topAdCreativesByReach.map(
    (creative) => creative.total_reach,
  );
  const adCreativeCtrLabels = topAdCreativesByCtr.map(
    (creative) => creative.ad_name,
  );
  const adCreativeCtrValues = topAdCreativesByCtr.map((creative) => {
    const ctr = parseCtrValue(creative.avg_ctr) ?? 0;
    return Math.max(ctr * 100, 0);
  });

  const audienceCreativeTopLimit = 5;
  const hasAudienceCreativePerformance = audienceCreativePerformance.length > 0;
  const sortedAudienceCreativePerformance = hasAudienceCreativePerformance
    ? [...audienceCreativePerformance].sort(
        (a, b) => b.total_impressions - a.total_impressions,
      )
    : [];
  const audienceCreativeAudienceLimit = 6;
  const audienceCreativeCards = sortedAudienceCreativePerformance.slice(
    0,
    audienceCreativeAudienceLimit,
  );

  const summaryEngagementRate = calculateEngagementRatePercent(
    summary,
    engagementRateMode,
    manualFollowerCount,
  );
  const avgCTR = summary.avg_ctr ? parseFloat(summary.avg_ctr) * 100 : null;
  const followerDataAvailable = useMemo(
    () =>
      (summary.total_followers ?? 0) > 0 ||
      platforms.some((platform) => (platform.total_followers ?? 0) > 0) ||
      campaigns.some((campaign) => (campaign.total_followers ?? 0) > 0) ||
      ad_sets.some((adSet) => (adSet.total_followers ?? 0) > 0) ||
      (manualFollowerCount ?? 0) > 0,
    [
      summary.total_followers,
      platforms,
      campaigns,
      ad_sets,
      manualFollowerCount,
    ],
  );
  const currentEngagementOption = ENGAGEMENT_RATE_OPTIONS.find(
    (option) => option.id === engagementRateMode,
  );

  return (
    <div className={`py-16 px-8 ${PAGE_BG}`} data-dashboard>
      <div className="max-w-7xl mx-auto">
        {/* Campaign View Toggle */}
        <div
          className="flex justify-between items-center gap-4 mb-6"
          data-exclude-from-export
        >
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setCampaignView("active")}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition ${
                campaignView === "active"
                  ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/30"
                  : "bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              }`}
              aria-pressed={campaignView === "active"}
            >
              <Sparkles className="h-4 w-4" />
              Active Campaigns
            </button>
            <button
              type="button"
              onClick={() => setCampaignView("all")}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition ${
                campaignView === "all"
                  ? "bg-indigo-600 text-white shadow-lg shadow-indigo-500/30"
                  : "bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              }`}
              aria-pressed={campaignView === "all"}
            >
              <LayoutList className="h-4 w-4" />
              All Campaigns
            </button>
            <button
              type="button"
              onClick={() => setCampaignView("archive")}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition ${
                campaignView === "archive"
                  ? "bg-blue-600 text-white shadow-lg shadow-blue-500/30"
                  : "bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              }`}
              aria-pressed={campaignView === "archive"}
            >
              <Archive className="h-4 w-4" />
              Archive Campaigns
            </button>
          </div>

          {/* Influencer exclusion toggle */}
          <div className="flex items-center gap-3">
            <label
              htmlFor="exclude-influencer-ads"
              className="text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Exclude Influencer Ads
            </label>
            <button
              type="button"
              id="exclude-influencer-ads"
              onClick={() => setExcludeInfluencerAds(!excludeInfluencerAds)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                excludeInfluencerAds ? "bg-red-600" : "bg-gray-300 dark:bg-gray-600"
              }`}
              role="switch"
              aria-checked={excludeInfluencerAds}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  excludeInfluencerAds ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {Array.from(EXCLUDED_INFLUENCER_AD_NAMES).join(", ")}
            </span>
          </div>
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Social Media Campaign Report: Meta
                {campaignView === "archive" && (
                  <span className="ml-3 text-xl text-gray-600 dark:text-gray-400">
                    (Archive)
                  </span>
                )}
                {campaignView === "all" && (
                  <span className="ml-3 text-xl text-gray-600 dark:text-gray-400">
                    (All Campaigns)
                  </span>
                )}
              </h1>
              <div className="flex items-center gap-4 mt-3 text-sm text-gray-600 dark:text-gray-400">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                  Social Media
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {summary.date_range_start} to {summary.date_range_end}
                </span>
                <span className="flex items-center gap-1">
                  <BarChart3 className="w-3 h-3" />
                  {data.records_processed} records
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {summary.total_days} days
                </span>
              </div>
            </div>
            <div>
                <ClientLogo
                  clientId={resolvedClientId}
                className="h-16 object-contain"
                fallback={
                  <div className="h-16 w-16 bg-gray-200 dark:bg-[#5B5353] rounded" />
                }
              />
            </div>
          </div>
        </div>
        {/* Key Performance Metrics
        <div
          className="flex flex-wrap items-center justify-end gap-3 mb-6"
          data-exclude-from-export
        >
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Engagement Rate Basis:
          </span>
          <div className="flex rounded-full border border-gray-200 bg-white shadow-sm overflow-hidden dark:border-gray-700 dark:bg-gray-900">
            {ENGAGEMENT_RATE_OPTIONS.map((option, index) => {
              const isActive = engagementRateMode === option.id;
              const isFollowersOption = option.id === "followers";
              const isDataLight =
                isFollowersOption &&
                !followerDataAvailable &&
                !manualFollowerCount;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setEngagementRateMode(option.id)}
                  className={`relative px-4 py-1.5 text-xs font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 first:rounded-l-full last:rounded-r-full rounded-full ${
                    isActive
                      ? "bg-blue-600 text-white shadow-md"
                      : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                  } ${isDataLight ? "opacity-70" : ""}`}
                  aria-pressed={isActive}
                  aria-label={`Engagement rate ${option.label}`}
                  title={option.helper}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          {currentEngagementOption && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {currentEngagementOption.helper}
            </span>
          )}
          {engagementRateMode === "followers" && (
            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <label htmlFor="manual-follower-count" className="sr-only">
                Total followers
              </label>
              <input
                id="manual-follower-count"
                type="number"
                min={0}
                value={manualFollowerCount ?? ""}
                onChange={(event) => {
                  const { value } = event.target;
                  if (value === "") {
                    setManualFollowerCount(null);
                    return;
                  }
                  const parsed = Number(value);
                  if (Number.isNaN(parsed) || parsed < 0) {
                    return;
                  }
                  setManualFollowerCount(Math.floor(parsed));
                }}
                placeholder="Enter followers"
                className="w-36 rounded-lg border border-gray-300 px-3 py-1 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
              />
              {summary.total_followers !== null &&
              summary.total_followers !== undefined &&
              summary.total_followers > 0 &&
              summary.total_followers !== manualFollowerCount ? (
                <button
                  type="button"
                  onClick={() =>
                    setManualFollowerCount(summary.total_followers ?? null)
                  }
                  className="rounded-full border border-transparent bg-gray-200 px-3 py-1 text-xs font-medium text-gray-700 transition hover:bg-gray-300 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  Use dataset total (
                  {summary.total_followers?.toLocaleString() ?? "0"})
                </button>
              ) : null}
              {manualFollowerCount === null && (
                <span className="text-xs text-red-600 dark:text-red-400">
                  Provide a follower count to calculate engagement rate.
                </span>
              )}
            </div>
          )}
        </div>
        */}

        {/* Key Performance Metrics */}
        <SectionToggleWrapper
          sectionId="social-overview"
          title="Performance Overview"
          hidden={isSectionHidden("social-overview")}
          onHide={hideSection}
          onRestore={restoreSection}
          className="mb-8"
        >
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Performance Overview
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Total Reach"
              value={formatNumber(summary.total_reach)}
              subtitle="Unique users reached"
              trend="neutral"
              icon={<Users className="w-5 h-5" />}
            />
            <MetricCard
              title="Total Impressions"
              value={formatNumber(summary.total_impressions)}
              subtitle="Total times ads were shown"
              trend="neutral"
              icon={<Eye className="w-5 h-5" />}
            />
            <MetricCard
              title="Total Engagements"
              value={formatNumber(summary.total_engagement)}
              subtitle={`Reactions, Comments, Shares`}
              trend="neutral"
              icon={<Zap className="w-5 h-5" />}
            />
            <MetricCard
              title="Frequency"
              value={parseFloat(summary.avg_frequency).toFixed(2)}
              subtitle="Avg impressions per user"
              trend="neutral"
              icon={<Clock className="w-5 h-5" />}
            />
          </div>
        </SectionToggleWrapper>

        {/* Platform Performance */}
        <SectionToggleWrapper
          sectionId="social-platform-performance"
          title="Platform Performance"
          hidden={isSectionHidden("social-platform-performance")}
          onHide={hideSection}
          onRestore={restoreSection}
          className="mb-8"
        >
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Platform Performance
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Platform Distribution Donut */}
            <div
              className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Impressions by Platform
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Total impressions: {summary.total_impressions.toLocaleString()}
              </p>
              <DonutChart
                data={platformChartData}
                colors={platformChartColors}
              />
            </div>

            {/* Platform Comparison Cards */}
            <div className="space-y-4">
              {platforms.map((platform) => {
                const platformEngagementRate = calculateEngagementRatePercent(
                  platform,
                  engagementRateMode,
                );
                return (
                  <div
                    key={platform.platform}
                    className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
                    style={{
                      borderLeftWidth: "4px",
                      borderLeftColor: getPlatformColor(platform.platform),
                    }}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {platform.platform.charAt(0).toUpperCase() +
                          platform.platform.slice(1)}
                      </h3>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {platform.campaign_count} campaigns •{" "}
                        {platform.ad_set_count} ad sets
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Impressions
                        </div>
                        <div className="text-base font-semibold text-gray-900 dark:text-white">
                          {formatNumber(platform.total_impressions)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Total Engagements
                        </div>
                        <div className="text-base font-semibold text-gray-900 dark:text-white">
                          {formatNumber(platform.total_engagement)}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Reactions
                        </div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatNumber(platform.total_reactions)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Comments
                        </div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatNumber(platform.total_comments)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Shares
                        </div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatNumber(platform.total_shares)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </SectionToggleWrapper>

        {/* Audience Performance */}
        {hasAudienceData && (
          <SectionToggleWrapper
            sectionId="social-audience-performance"
            title="Audience Performance"
            hidden={isSectionHidden("social-audience-performance")}
            onHide={hideSection}
            onRestore={restoreSection}
            className="mb-8"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  Audience Performance Insights
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Audience refers to Meta ad sets. Visuals highlight leading
                  audiences across reach and click-through performance.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div
                className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
              >
                <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                  Top Audiences by Reach
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Highest-reach audiences over the selected period.
                </p>
                <BarChart
                  labels={audienceReachLabels}
                  datasets={[
                    {
                      label: "Reach",
                      data: audienceReachValues,
                      backgroundColor: "rgba(59, 130, 246, 0.85)",
                    },
                  ]}
                  horizontal={true}
                  showLegend={false}
                  height="h-56"
                  valueFormatter={(value) =>
                    formatNumber(Number.isFinite(value) ? value : 0)
                  }
                />
              </div>

              <div
                className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
              >
                <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                  Top Audiences by CTR
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Highest click-through rates among Meta audiences.
                </p>
                <BarChart
                  labels={audienceCtrLabels}
                  datasets={[
                    {
                      label: "CTR",
                      data: audienceCtrValues,
                      backgroundColor: "rgba(14, 165, 233, 0.85)",
                    },
                  ]}
                  horizontal={true}
                  showLegend={false}
                  height="h-56"
                  valueFormatter={(value) =>
                    Number.isFinite(value) ? `${value.toFixed(2)}%` : "0%"
                  }
                />
              </div>
            </div>
          </SectionToggleWrapper>
        )}

        {/* Ad Creative Performance */}
        {hasAdCreativeData && (
          <SectionToggleWrapper
            sectionId="social-ad-creative-performance"
            title="Ad Creative Performance"
            hidden={isSectionHidden("social-ad-creative-performance")}
            onHide={hideSection}
            onRestore={restoreSection}
            className="mb-8"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  Ad Creative Performance Insights
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Ad Creative refers to individual ads within Meta ad sets.
                  Metrics are distributed across creatives when multiple ads
                  exist in a set.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div
                className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
              >
                <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                  Top Ad Creatives by Reach
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Highest-reach ad creatives across the reporting window.
                </p>
                <BarChart
                  labels={adCreativeReachLabels}
                  datasets={[
                    {
                      label: "Reach",
                      data: adCreativeReachValues,
                      backgroundColor: "rgba(79, 70, 229, 0.85)",
                    },
                  ]}
                  horizontal={true}
                  showLegend={false}
                  valueFormatter={(value) =>
                    formatNumber(Number.isFinite(value) ? value : 0)
                  }
                />
              </div>

              <div
                className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
              >
                <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                  Top Ad Creatives by CTR
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Creative variations driving the strongest click-through rates.
                </p>
                <BarChart
                  labels={adCreativeCtrLabels}
                  datasets={[
                    {
                      label: "CTR",
                      data: adCreativeCtrValues,
                      backgroundColor: "rgba(249, 115, 22, 0.85)",
                    },
                  ]}
                  horizontal={true}
                  showLegend={false}
                  valueFormatter={(value) =>
                    Number.isFinite(value) ? `${value.toFixed(2)}%` : "0%"
                  }
                />
              </div>
            </div>
          </SectionToggleWrapper>
        )}

        {hasAudienceCreativePerformance && (
          <SectionToggleWrapper
            sectionId="social-audience-creative-performance"
            title="Audience & Creative Pairings"
            hidden={isSectionHidden("social-audience-creative-performance")}
            onHide={hideSection}
            onRestore={restoreSection}
            className="mb-8"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  Audience & Creative Matchups
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Visualizes how each audience interacts with top creatives,
                  ranked by impression share and click-through rate.
                </p>
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Showing up to {audienceCreativeAudienceLimit} audiences •{" "}
                {audienceCreativeTopLimit} creatives each
              </span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {audienceCreativeCards.map((audience) => {
                const topCreatives = audience.creatives.slice(
                  0,
                  audienceCreativeTopLimit,
                );
                return (
                  <div
                    key={audience.ad_set_name}
                    className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                          {audience.ad_set_name}
                        </h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {formatNumber(audience.total_impressions)} impressions
                          • {audience.creatives.length} creatives
                        </p>
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 text-right">
                        <div>Reach</div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatNumber(audience.total_reach)}
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {topCreatives.map((creative) => {
                        const share =
                          audience.total_impressions > 0
                            ? creative.total_impressions /
                              audience.total_impressions
                            : 0;
                        const sharePercent = share * 100;
                        const ctrPercent =
                          creative.avg_ctr !== null
                            ? creative.avg_ctr * 100
                            : null;
                        return (
                          <div
                            key={`${audience.ad_set_name}-${creative.ad_name}`}
                            className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-gray-800/40 p-4"
                          >
                            <div className="flex items-start justify-between gap-4 mb-2">
                              <div>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {creative.ad_name}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {formatPercent(sharePercent)} of audience
                                  impressions
                                </div>
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 text-right">
                                CTR
                                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                  {formatPercent(ctrPercent)}
                                </div>
                              </div>
                            </div>
                            <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden mb-3">
                              <div
                                className="h-full rounded-full bg-blue-500/80"
                                style={{
                                  width: `${Math.max(
                                    0,
                                    Math.min(sharePercent, 100),
                                  )}%`,
                                }}
                              />
                            </div>
                            <div className="grid grid-cols-3 gap-4 text-xs text-gray-600 dark:text-gray-400">
                              <div>
                                <div className="uppercase tracking-wide text-[10px]">
                                  Impressions
                                </div>
                                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                  {formatNumber(creative.total_impressions)}
                                </div>
                              </div>
                              <div>
                                <div className="uppercase tracking-wide text-[10px]">
                                  Reach
                                </div>
                                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                  {formatNumber(creative.total_reach)}
                                </div>
                              </div>
                              <div>
                                <div className="uppercase tracking-wide text-[10px]">
                                  Engagements
                                </div>
                                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                  {formatNumber(creative.total_engagement)}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionToggleWrapper>
        )}

        {/* Engagement Breakdown
        <SectionToggleWrapper
          sectionId="social-engagement-breakdown"
          title="Engagement Breakdown"
          hidden={isSectionHidden("social-engagement-breakdown")}
          onHide={hideSection}
          onRestore={restoreSection}
          className="mb-8"
        >
          <div
            className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Engagement Type Distribution
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Total engagement: {summary.total_engagement.toLocaleString()}
            </p>
            <BarChart
              labels={engagementLabels}
              datasets={[
                {
                  label: "Engagement Count",
                  data: engagementValues,
                  backgroundColor: engagementColors,
                },
              ]}
              horizontal={true}
              showLegend={false}
              height="h-64"
            />
          </div>
        </SectionToggleWrapper>
        */}

        {/* Daily Performance Trend
        <SectionToggleWrapper
          sectionId="social-daily-trend"
          title="Daily Performance Trend"
          hidden={isSectionHidden("social-daily-trend")}
          onHide={hideSection}
          onRestore={restoreSection}
          className="mb-8"
        >
          <div
            className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Daily Performance Trend
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Reach & Engagement over {summary.total_days} days
            </p>
            <BarChart
              labels={dailyLabels}
              datasets={dailyDatasets}
              stacked={false}
              horizontal={false}
              showLegend={true}
              height="h-96"
            />
          </div>
        </SectionToggleWrapper>
        */}

        {/* Ad Set Performance Table */}
        {ad_sets && ad_sets.length > 0 && (
          <SectionToggleWrapper
            sectionId="social-ad-set-performance"
            title="Ad Set Performance"
            hidden={isSectionHidden("social-ad-set-performance")}
            onHide={hideSection}
            onRestore={restoreSection}
            className="mb-8"
          >
            <div
              className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Ad Set Performance
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {ad_sets.length} ad sets sorted by reach
              </p>
              <div className="overflow-x-auto" data-export-disable-scroll>
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Ad Set
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Platform
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Reach
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Engagement
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Engagement Rate
                      </th>
                    </tr>
                  </thead>
                  <tbody
                    className={`${CARD_BG} divide-y divide-gray-200 dark:divide-gray-700`}
                  >
                    {[...ad_sets]
                      .sort((a, b) => b.total_reach - a.total_reach)
                      .map((adSet) => {
                        const adSetEngagementRate =
                          calculateEngagementRatePercent(
                            adSet,
                            engagementRateMode,
                          );
                        return (
                          <tr
                            key={`${adSet.campaign_name}-${adSet.ad_set_name}`}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          >
                            <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white max-w-xs truncate">
                              {adSet.ad_set_name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                style={{
                                  backgroundColor:
                                    getPlatformColor(adSet.platform) + "20",
                                  color: getPlatformColor(adSet.platform),
                                }}
                              >
                                {adSet.platform.charAt(0).toUpperCase() +
                                  adSet.platform.slice(1)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-900 dark:text-white">
                              {adSet.total_reach.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-400">
                              {adSet.total_engagement.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${engagementBadgeClasses(adSetEngagementRate)}`}
                              >
                                {formatEngagementRatePercent(
                                  adSetEngagementRate,
                                )}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </SectionToggleWrapper>
        )}

        {/* Campaign Performance Table */}
        <SectionToggleWrapper
          sectionId="social-campaign-performance"
          title="Campaign Performance"
          hidden={isSectionHidden("social-campaign-performance")}
          onHide={hideSection}
          onRestore={restoreSection}
        >
          <div
            className={`${CARD_BG} rounded-lg ${SHADOW.card} border border-gray-200 dark:border-gray-700 p-6`}
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Campaign Performance
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {summary.campaign_count} campaigns sorted by reach
            </p>
            <div className="overflow-x-auto" data-export-disable-scroll>
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Campaign
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Reach
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Impressions
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Engagement
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Engagement Rate
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Ad Sets
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={`${CARD_BG} divide-y divide-gray-200 dark:divide-gray-700`}
                >
                  {[...campaigns]
                    .sort((a, b) => b.total_reach - a.total_reach)
                    .map((campaign) => {
                      const campaignEngagementRate =
                        calculateEngagementRatePercent(
                          campaign,
                          engagementRateMode,
                        );
                      return (
                        <tr
                          key={campaign.campaign_name}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white max-w-md truncate">
                            {campaign.campaign_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-900 dark:text-white">
                            {campaign.total_reach.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-400">
                            {campaign.total_impressions.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-400">
                            {campaign.total_engagement.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${engagementBadgeClasses(campaignEngagementRate)}`}
                            >
                              {formatEngagementRatePercent(
                                campaignEngagementRate,
                              )}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-400">
                            {campaign.ad_set_count}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </SectionToggleWrapper>
      </div>
    </div>
  );
};
